import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Bell, Globe, Moon, Sun, User, LogOut, RefreshCw, CalendarDays } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { OnlineIndicator } from '@/components/OnlineIndicator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';

export default function TopHeader() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [now, setNow] = useState(new Date());
  const isRTL = i18n.language === 'ar';

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('just_now') || 'Just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  const handleNotifClick = (n) => {
    if (!n.read) {
      api.put(`/api/notifications/${n.id}/read`).catch(() => {});
      setNotifications(prev => ({
        ...prev,
        unread_count: Math.max(0, prev.unread_count - 1),
        notifications: prev.notifications.map(x => x.id === n.id ? { ...x, read: true } : x),
      }));
    }
    if (n.request_id) {
      setTimeout(() => navigate(`/requests/${n.request_id}`), 100);
    }
  };

  const getNotifAvatar = (n) => {
    if (n.sponsor_logo_url) {
      return <img src={n.sponsor_logo_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5 border border-gray-100 dark:border-gray-700" />;
    }
    if (n.sponsor_name) {
      const initials = n.sponsor_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      return <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-[10px] font-bold text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5">{initials}</div>;
    }
    const typeColors = { new_request: 'bg-blue-500', request_approved: 'bg-green-500', request_rejected: 'bg-red-500', cancellation_requested: 'bg-amber-500', delivery_confirmed: 'bg-purple-500' };
    const color = typeColors[n.type] || 'bg-gray-400';
    return <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center shrink-0 mt-0.5`}><Bell size={14} className="text-white" /></div>;
  };

  useEffect(() => {
    api.get('/api/notifications').then(r => setNotifications(r.data)).catch(() => {});
    const notifInterval = setInterval(() => {
      api.get('/api/notifications').then(r => setNotifications(r.data)).catch(() => {});
    }, 30000);
    const clockInterval = setInterval(() => setNow(new Date()), 30000);
    return () => { clearInterval(notifInterval); clearInterval(clockInterval); };
  }, []);

  const toggleLang = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const markAllRead = async () => {
    await api.put('/api/notifications/read-all');
    setNotifications(prev => ({ ...prev, unread_count: 0, notifications: prev.notifications.map(n => ({ ...n, read: true })) }));
  };

  const dayName = now.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { weekday: 'long' });
  const dateStr = now.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = now.toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <header data-testid="top-header" className="hidden lg:flex items-center justify-between h-11 mb-5">
      {/* Left: Date & Time */}
      <div className="flex items-center gap-2.5 text-gray-500 dark:text-gray-400">
        <CalendarDays size={17} className="text-gray-400 dark:text-gray-500 shrink-0" />
        <span className="text-[13px] font-medium text-[var(--ink-2)]">{dayName},</span>
        <span className="text-[13px] font-medium text-[var(--ink-2)]">{dateStr}</span>
        <span className="w-px h-4 bg-[var(--control)] mx-1" />
        <span className="text-[13px] tabular-nums font-medium text-[var(--ink-2)]">{timeStr}</span>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        <Button onClick={() => window.location.reload()} data-testid="refresh-page-btn" variant="outline" size="sm"
          className="h-[38px] px-4 text-[13px] font-medium rounded-full bg-[var(--surface)] border border-[var(--control)] text-[var(--ink-2)]">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>

        <button onClick={toggleLang} data-testid="top-lang-toggle"
          className="flex items-center gap-1.5 h-[38px] px-4 rounded-full bg-[var(--surface)] border border-[var(--control)] text-[13px] font-medium text-[var(--ink-2)] hover:bg-[var(--chip)] transition-colors">
          <Globe size={16} />
          <span>{i18n.language === 'en' ? 'AR' : 'EN'}</span>
        </button>

        <button onClick={toggleTheme} data-testid="top-theme-toggle"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="flex items-center gap-1.5 h-[38px] px-4 rounded-full bg-[var(--surface)] border border-[var(--control)] text-[13px] font-medium text-[var(--ink-2)] hover:bg-[var(--chip)] transition-colors">
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
        </button>


        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="top-notification-bell" className="relative h-[38px] w-[38px] flex items-center justify-center rounded-full bg-[var(--surface)] border border-[var(--control)] text-[var(--ink-2)] hover:bg-[var(--chip)] transition-colors">
              <Bell size={18} />
              {notifications.unread_count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[var(--alert)] text-white text-[10px] font-medium tabular-nums rounded-full px-1 ring-2 ring-[var(--page)]">
                  {notifications.unread_count > 9 ? '9+' : notifications.unread_count}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
 <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-80 bg-white dark:bg-gray-900 dark: shadow-card rounded-card">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('notifications')}</span>
              {notifications.unread_count > 0 && (
                <button onClick={markAllRead} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">{t('mark_all_read')}</button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">{t('no_notifications')}</div>
              ) : (
                notifications.notifications.map(n => (
                  <div key={n.id} onClick={() => handleNotifClick(n)}
                    className={`px-4 py-3 border-b border-gray-50 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${!n.read ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''}`}>
                    <div className="flex items-start gap-2.5">
                      {getNotifAvatar(n)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-medium ${!n.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>{n.title}</p>
                          <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2" />}
                    </div>
                  </div>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-trigger" className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer">
              <div className="relative">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gray-900 dark:bg-gray-200 flex items-center justify-center text-white dark:text-gray-900 text-xs font-semibold">
                    {user?.name?.[0] || '?'}
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5"><OnlineIndicator isOnline={true} size="xs" /></span>
              </div>
              <div className="hidden xl:block min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate leading-tight text-left">{user?.name}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate text-left">{user?.role?.replace('_', ' ')}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
 <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48 bg-white dark:bg-gray-900 dark: shadow-card rounded-card">
            <DropdownMenuItem data-testid="profile-menu-profile" onClick={() => navigate('/profile')} className="cursor-pointer gap-2 text-sm text-gray-700 dark:text-gray-300">
              <User size={15} /> {t('profile') || 'Profile'}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="dark-mode-header-toggle" onClick={toggleTheme} className="cursor-pointer gap-2 text-sm text-gray-700 dark:text-gray-300">
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />} {theme === 'dark' ? (t('light_mode', 'Light Mode')) : (t('dark_mode', 'Dark Mode'))}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-100 dark:bg-gray-800" />
            <DropdownMenuItem data-testid="profile-menu-logout" onClick={() => { logout(); navigate('/login'); }} className="cursor-pointer gap-2 text-sm text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950">
              <LogOut size={15} /> {t('logout') || 'Logout'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
